// /src/pages/EditProduct.jsx
import React, { useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { motion } from "framer-motion";
import Select from "react-select";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import toast from "react-hot-toast";
import {
  useGetProductByIdQuery,
  useUpdateProductMutation,
} from "../toolkitServices/productApi";
import { useGetAllCategoriesQuery } from "../toolkitServices/categoryApi";

// ---------- Shared UI tokens (matches CreateDesign/CreateProduct) ----------
const baseInput =
  "w-full px-3 py-2.5 border rounded-lg text-[14px] focus:outline-none";
const okRing =
  "border-gray-300 focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]";
const errRing =
  "border-red-400 focus:ring-4 focus:ring-red-200 focus:border-red-500";

const makeSelectStyles = (hasError) => ({
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    height: 40,
    borderWidth: 1,
    borderColor: hasError ? "#ef4444" : state.isFocused ? "#9481ce" : "#e5e7eb",
    boxShadow: "none",
    ":hover": { borderColor: hasError ? "#ef4444" : "#9481ce" },
    borderRadius: 10,
    fontSize: "0.92rem",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 10px" }),
  indicatorsContainer: (base) => ({ ...base, height: 40 }),
  menu: (base) => ({ ...base, borderRadius: 10, overflow: "hidden" }),
  option: (base, state) => ({
    ...base,
    padding: "8px 12px",
    backgroundColor: state.isSelected ? "#efeafd" : state.isFocused ? "#f7f6ff" : "white",
    color: "#111827",
  }),
  placeholder: (base) => ({ ...base, color: "#9ca3af" }),
  singleValue: (base) => ({ ...base, color: "#111827" }),
});

const EditProduct = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // fetch product + categories
  const { data: prodResp, isLoading: isProdLoading, error: prodErr } =
    useGetProductByIdQuery(id, { skip: !id });
  const product = prodResp?.data;

  const { data: catResp, isLoading: isCatLoading } = useGetAllCategoriesQuery({
    page: 1,
    limit: 200,
    search: "",
  });
  const categories = catResp?.data || [];

  const categoryOptions = useMemo(
    () => categories.map((c) => ({ label: c.name, value: c._id })),
    [categories]
  );

  const [updateProduct, { isLoading: isSaving }] = useUpdateProductMutation();

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting, isDirty },
    setValue,
    setFocus,
  } = useForm({
    defaultValues: {
      productName: "",
      shortname: "",
      slug: "",
      description: "",
      category: null,
    },
    mode: "onBlur",
  });

  // hydrate form once product arrives
  useEffect(() => {
    if (product) {
      reset({
        productName: product.productName || "",
        shortname: product.shortName || product.shortname || "",
        slug: product.slug || "",
        description: product.description || "",
        // category set below
      });

      const currentCatId = product.categoryId || product?.category?._id;
      const currentCatName = product.categoryName || product?.category?.name;
      if (currentCatId && currentCatName) {
        setValue("category", { label: currentCatName, value: currentCatId });
      }

      // focus name field right after values are set
      requestAnimationFrame(() => setTimeout(() => setFocus("productName"), 0));
    }
  }, [product, reset, setValue, setFocus]);

  // focus again once the entrance animation completes (safeguard)
  const handleAnimDone = () => {
    requestAnimationFrame(() => setFocus("productName"));
  };

  // keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      // back
      if (e.ctrlKey && e.key === "Backspace") {
        e.preventDefault();
        navigate(-1);
      }
      // save
      if ((e.ctrlKey && e.key.toLowerCase() === "s") || (e.key === "Enter" && e.ctrlKey)) {
        e.preventDefault();
        handleSubmit(onSubmit)();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [navigate, handleSubmit]);

  const onSubmit = async (form) => {
    if (!id) return;
    const payload = {
      productName: (form.productName || "").trim(),
      shortname: (form.shortname || "").trim().toUpperCase(),
      slug: (form.slug || "").trim(),
      description: (form.description || "").trim(),
      categoryId: form.category?.value,
      categoryName: form.category?.label,
    };

    try {
      await updateProduct({ id, ...payload }).unwrap();
      toast.success("Product updated!");
      navigate("/masters/product-master-list"); // ✅ redirect to list
    } catch (e) {
      const msg = e?.data?.msg || e?.data?.message || e?.error || "Update failed";
      toast.error(msg);
      requestAnimationFrame(() => setFocus("productName"));
    }
  };

  if (isProdLoading) {
    return <div className="p-8 text-center text-gray-500">Loading product…</div>;
  }
  if (prodErr || !product) {
    return <div className="p-8 text-center text-red-500">Failed to load product</div>;
  }

  const loading = isSaving || isSubmitting;

  return (
    <div className="px-4 py-4">
      {/* Compact Header (matches other screens) */}
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-2 py-2 flex items-center gap-3">
          <Link
            to={"/masters/product-master-list"}
            className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-gray-200 hover:bg-gray-100 transition"
            title="Back to Product List"
          >
            <FaArrowLeft className="text-[#6f5cc5] text-sm" />
          </Link>
          <div className="flex-1">
            <h2 className="text-xl md:text-2xl font-semibold text-[#4b3d91] leading-tight">
              Edit Product
            </h2>
            <p className="text-xs text-gray-500">
              Tips: <kbd className="px-1 bg-gray-100 rounded border">Ctrl</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">S</kbd> or{" "}
              <kbd className="px-1 bg-gray-100 rounded border">Ctrl</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">Enter</kbd> to save,
                <kbd className="px-1 bg-gray-100 rounded border ml-2">Ctrl</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">Back Spc</kbd> go to list
            </p>
          </div>
        </div>
      </div>

      {/* Card — compact, no inner scroll; BORDER-ONLY validation */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28 }}
        onAnimationComplete={handleAnimDone}
        className="max-w-5xl mx-auto bg-white shadow-md rounded-xl mt-4 border border-gray-200 relative"
      >
        {/* Loading overlay */}
        {loading && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center z-20 rounded-xl">
            <div className="animate-spin h-7 w-7 border-2 border-[#9481ce] border-t-transparent rounded-full" />
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 md:grid-cols-2 gap-4 p-5">
          {/* Product Name */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              Product Name<span className="text-red-500">*</span>
            </label>
            <input
              {...register("productName", { required: true, minLength: 2, maxLength: 120 })}
              aria-invalid={!!errors.productName}
              placeholder="Enter product name"
              autoComplete="off"
              className={`${baseInput} ${!!errors.productName ? errRing : okRing}`}
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              Category<span className="text-red-500">*</span>
            </label>
            <Controller
              name="category"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={categoryOptions}
                  isLoading={isCatLoading}
                  placeholder="Select category"
                  classNamePrefix="react-select"
                  styles={makeSelectStyles(!!errors.category)}
                />
              )}
            />
          </div>

          {/* Short Name */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">Short Name</label>
            <input
              {...register("shortname")}
              placeholder="Short name (e.g. PROD01)"
              autoComplete="off"
              className={`${baseInput} ${okRing}`}
            />
          </div>

          {/* Slug */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">Slug</label>
            <input
              {...register("slug")}
              placeholder="product-slug-here"
              autoComplete="off"
              className={`${baseInput} ${okRing}`}
            />
          </div>

          {/* Description (full width) */}
          <div className="md:col-span-2">
            <label className="block text-[13px] font-medium text-gray-700 mb-1">Description</label>
            <textarea
              {...register("description")}
              placeholder="Enter description"
              rows={2}
              className={`${baseInput} ${okRing} resize-y`}
            />
          </div>

          {/* Submit */}
          <div className="md:col-span-2 flex flex-col sm:flex-row gap-2 pt-1">
            <motion.button
              whileHover={{ scale: loading ? 1 : 1.01 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
              type="submit"
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium shadow-sm bg-[#9481ce] hover:bg-[#7d6ec0] text-white disabled:opacity-60"
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </motion.button>

            <Link
              to="/masters/product-master-list"
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium border border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
              title="Cancel and go back"
            >
              Cancel
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default EditProduct;
